import apiClient from './apiClient';

// Detectar si estamos en el subdominio blog
const isBlogSubdomain = window.location.hostname.startsWith('blog.')

// Función para obtener la URL base del blog según el subdominio
const getBlogBaseURL = () => {
  if (isBlogSubdomain) {
    // Si estamos en blog.site.tld, usar URLs sin prefijo /blog/
    return ''
  }
  // Si estamos en el dominio principal, usar /blog/
  return '/blog'
}

// Servicios para categorías del blog
export const getBlogCategories = async () => {
  try {
    const baseURL = getBlogBaseURL()
    console.log('Obteniendo categorías desde:', `${baseURL}/categories/`)
    const response = await apiClient.get(`${baseURL}/categories/`);
    console.log('Categorías obtenidas:', response.data);
    
    // Asegurar que devolvemos un array
    if (Array.isArray(response.data)) {
      return response.data;
    } else if (response.data && Array.isArray(response.data.results)) {
      return response.data.results;
    } else {
      console.warn('Formato de respuesta inesperado:', response.data);
      return [];
    }
  } catch (error) {
    console.error('Error fetching blog categories:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

export const createBlogCategory = async (categoryData) => {
  try {
    const baseURL = getBlogBaseURL()
    console.log('Creando categoría:', categoryData);
    const response = await apiClient.post(`${baseURL}/categories/`, categoryData);
    console.log('Categoría creada:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating blog category:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

export const updateBlogCategory = async (id, categoryData) => {
  try {
    const baseURL = getBlogBaseURL()
    console.log('Actualizando categoría:', id, categoryData);
    const response = await apiClient.put(`${baseURL}/categories/${id}/`, categoryData);
    console.log('Categoría actualizada:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error updating blog category:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

export const deleteBlogCategory = async (id) => {
  try {
    const baseURL = getBlogBaseURL()
    console.log('Eliminando categoría:', id);
    const response = await apiClient.delete(`${baseURL}/categories/${id}/`);
    console.log('Categoría eliminada');
    return response.data;
  } catch (error) {
    console.error('Error deleting blog category:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

// Servicios para posts del blog
export const getBlogPosts = async (params = {}) => {
  try {
    const baseURL = getBlogBaseURL()
    const response = await apiClient.get(`${baseURL}/posts/`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    throw error;
  }
};

export const getBlogPost = async (id) => {
  try {
    const baseURL = getBlogBaseURL()
    const response = await apiClient.get(`${baseURL}/posts/${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching blog post:', error);
    throw error;
  }
};

export const getBlogPostBySlug = async (slugOrId) => {
  try {
    console.log('Buscando post con slug o ID:', slugOrId);
    const baseURL = getBlogBaseURL()
    
    // Primero intentar buscar por ID si es un número
    if (!isNaN(slugOrId)) {
      console.log('Buscando por ID:', slugOrId);
      const response = await apiClient.get(`${baseURL}/posts/${slugOrId}/`);
      console.log('Post encontrado por ID:', response.data);
      return response.data;
    }
    
    // Si no es un número, buscar por slug
    console.log('Buscando por slug:', slugOrId);
    
    // Obtener todos los posts y buscar por slug
    const response = await apiClient.get(`${baseURL}/posts/`, { 
      params: { 
        status: 'published',
        page_size: 100 // Obtener más posts para asegurar encontrar el slug
      } 
    });
    
    console.log('Posts obtenidos para buscar slug:', response.data.results);
    
    // Buscar por slug exacto en los resultados
    const post = response.data.results?.find(p => p.slug === slugOrId);
    
    if (!post) {
      console.error('Post no encontrado con slug:', slugOrId);
      throw new Error(`Post no encontrado con slug: ${slugOrId}`);
    }
    
    console.log('Post encontrado por slug:', post);
    return post;
  } catch (error) {
    console.error('Error fetching blog post by slug or ID:', error);
    throw error;
  }
};

export const createBlogPost = async (postData) => {
  try {
    const baseURL = getBlogBaseURL()
    const response = await apiClient.post(`${baseURL}/posts/`, postData);
    return response.data;
  } catch (error) {
    console.error('Error creating blog post:', error);
    throw error;
  }
};

export const updateBlogPost = async (id, postData) => {
  try {
    const baseURL = getBlogBaseURL()
    const response = await apiClient.put(`${baseURL}/posts/${id}/`, postData);
    return response.data;
  } catch (error) {
    console.error('Error updating blog post:', error);
    throw error;
  }
};

export const deleteBlogPost = async (id) => {
  try {
    const baseURL = getBlogBaseURL()
    const response = await apiClient.delete(`${baseURL}/posts/${id}/`);
    return response.data;
  } catch (error) {
    console.error('Error deleting blog post:', error);
    throw error;
  }
};

export const getFeaturedPosts = async () => {
  try {
    const baseURL = getBlogBaseURL()
    const response = await apiClient.get(`${baseURL}/posts/featured/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching featured posts:', error);
    throw error;
  }
};

export const getRecentPosts = async () => {
  try {
    const baseURL = getBlogBaseURL()
    const response = await apiClient.get(`${baseURL}/posts/recent/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching recent posts:', error);
    throw error;
  }
};

// Servicios para administración de posts
export const getAdminBlogPosts = async (params = {}) => {
  try {
    const baseURL = getBlogBaseURL()
    const response = await apiClient.get(`${baseURL}/posts/admin_list/`, { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching admin blog posts:', error);
    throw error;
  }
};

export const getAdminBlogPost = async (id) => {
  try {
    const baseURL = getBlogBaseURL()
    const response = await apiClient.get(`${baseURL}/posts/${id}/admin_retrieve/`);
    return response.data;
  } catch (error) {
    console.error('Error fetching admin blog post:', error);
    throw error;
  }
};

// Función para incrementar vistas
export const incrementPostViews = async (id) => {
  try {
    const baseURL = getBlogBaseURL()
    const response = await apiClient.post(`${baseURL}/posts/${id}/increment_views/`);
    return response.data;
  } catch (error) {
    console.error('Error incrementing post views:', error);
    // No lanzar error para no interrumpir la experiencia del usuario
  }
}; 